// message-box-view.ts — the DOM port of the HtMessageBox family (alerts / warnings /
// errors / Yes-No queries), the last un-ported modal family (survey I10). Renders the
// canvas DialogForm (kidfMessageBox 1029 OK-only, kidfMessageBoxQuery 1058 Yes/No)
// FAITHFULLY — same chrome the in-game ConfirmView uses (form.ts:1021-1068):
//   - a BLACK (or WHITE, kfMbWhiteBorder) DrawBorder frame (gcxyBorder);
//   - a RED title BAR (msgbox.ts:67 SetTitleColor(GetColor(kiclrRed)) — UNconditional:
//     EVERY HtMessageBox has a red title bar, alerts and warnings alike);
//   - a DOUBLE-shadow translucent body (SetBackgroundColorIndex(kiclrShadow2x) → two
//     ShadowHelper passes ⇒ ~0.63 black over the live canvas).
// The canvas form SELF-SIZES to the message: the multiline kidcMessage label grows its
// height, the form grows by the same delta and re-CENTERS on the screen (misc.cpp:502-560).
// Screen-CENTRED (NOT gob-anchored like ConfirmView) — verified against msgbox.ts /
// CreateHtMessageBox (it re-centres on the whole DIB, no gob).
//
// COMMAND-ADJACENT, DISPLAY-ONLY: this view emits NO command and constructs NO Message.
// It reports the choice through onResult(fOk) — the C++ HtMessageBox `done(f)` bool — and
// the host routes it to the SAME continuation the canvas DoModal uses (the resign/leave/
// exit/observe behaviour stays byte-identical, exactly like domConfirm).
//
// forms.ini.pp:
//   [kidfMessageBox]      FORM (0 0 140 50) default kidcOk
//     LABEL=kidcTitle   (61 2 18 0)  "Title"   kifntTitle  center
//     LABEL=kidcMessage (6 15 128 8) "Message" kifntShadow center multiline
//     BUTTON=kidcOk     (44 32 54 12) "OK"     kifntButton
//   [kidfMessageBoxQuery] FORM (0 0 140 50) default kidcOk
//     LABEL=kidcTitle   (61 2 18 0)  "Title"   kifntTitle  center
//     LABEL=kidcMessage (6 15 128 8) "Message" kifntShadow center multiline
//     BUTTON=kidcOk     (10 32 54 12) "Yes"    kifntButton
//     BUTTON=kidcCancel (74 32 54 12) "No"     kifntButton

import type { ButtonArt } from '../title-art.ts';
import {
  COL_FG,
  COL_BORDER_WHITE,
  COL_BORDER_BLACK,
  TEXT_SHADOW,
  el,
  FONT_BODY,
  buttonWidthU,
  formScale,
  at,
  formTitle,
  makeStageButton,
  type StageButtonSize,
  rootBox,
} from '../chrome.ts';

// forms.ini.pp [kidfMessageBox]/[kidfMessageBoxQuery] (FORM 0 0 140 50), design units.
const FORM = { w: 140, h: 50 };
const TITLE = { x: 61, y: 2, w: 18 }; // kidcTitle (centred; we span the form)
const MSG = { x: 6, y: 15, w: 128, h: 8 }; // kidcMessage (multiline, self-sizing)
const OK_ONLY = { x: 44, y: 32, w: 54, h: 12 }; // kidfMessageBox kidcOk "OK"
const YES = { x: 10, y: 32, w: 54, h: 12 }; // kidfMessageBoxQuery kidcOk "Yes"
const NO = { x: 74, y: 32, w: 54, h: 12 }; // kidfMessageBoxQuery kidcCancel "No"
// form.ts: kcyTitle=10 (the title-bar height); gcxyBorder ≈ 1 design unit (the frame).
const CY_TITLE = 10;
const BORDER = 1;

// the kiclr* the canvas reaches (palette.json): red title bar, white/black border.
const COL_TITLE_BAR = 'rgb(252, 0, 0)'; // kiclrRed — msgbox.ts SetTitleColor (always)
const FONT = FONT_BODY; // kifntShadow body = Eurostile
const shadow = TEXT_SHADOW;

export interface MessageBoxViewDeps {
  /** the overlay container (created by the host; this view owns its contents). */
  root: HTMLElement;
  /** the runtime title (kidcTitle), e.g. "Warning" / "GAME OVER" / "Server Message". */
  title: string;
  /** the message body (kidcMessage) — multiline, self-sizing; rendered without clipping. */
  message: string;
  /** the button set: 'ok' = kidfMessageBox (single OK); 'query' = kidfMessageBoxQuery
   *  (Yes + No). Matches the idf the C++ HtMessageBox loaded. */
  variant: 'ok' | 'query';
  /** kfMbWhiteBorder → a WHITE frame (the MP forms set it); else the black default. */
  whiteBorder?: boolean;
  /** the raw 9-slice button pieces — faithful chrome; absent ⇒ a CSS-button fallback. */
  buttonArt?: ButtonArt;
  /** MODELESS (non-autotakedown) — suppress BOTH the backdrop-click AND the Esc → onResult(false)
   *  dismiss handlers, so the box resolves ONLY on an explicit Yes/No button click. Set for the
   *  in-game LAG ALERT (lagform.ts / the domLagBox seam): the canvas lag DialogForms (forms.ini.pp
   *  1029/1058) LACK the kfFrmAutoTakedown token — the modeless GetResult() poll ignores outside
   *  input and waits for a real button press — so a stray backdrop-click / Esc must NOT resolve(false)
   *  here (false casts CsKillLaggingPlayer{fYes 0} — a spurious "keep the lagger" WIRE VOTE the
   *  canvas never sends). The default (unset) keeps the kfFrmAutoTakedown backdrop/Esc dismiss the
   *  resign/observe/generic boxes rely on (a LOCAL cancel — no wire vote). */
  modeless?: boolean;
  /** play a GUI sfx (ksfxGui*) on a button press, wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
  /** the user's choice → the host unmounts the overlay and routes this to the SAME
   *  continuation the canvas DoModal uses (the C++ `done(f)` bool). OK / Yes = true
   *  (kidcOk, the FORM default); No / backdrop / Esc = false (kidcCancel). For the
   *  OK-only variant there is no "false" button, but a backdrop/Esc dismiss reports
   *  the default (OK ⇒ true) so an info box still resolves like the canvas auto-takedown. */
  onResult: (fOk: boolean) => void;
}

export class MessageBoxView {
  private readonly d: MessageBoxViewDeps;
  /** the shared 9-slice button renderer (the small dialog fallback flavour). */
  private readonly btn: (label: string, on: () => void, size: StageButtonSize) => HTMLButtonElement;
  /** guards against a double-result (a click + an Esc landing together). */
  private resolved = false;

  constructor(d: MessageBoxViewDeps) {
    this.d = d;
    const mk = makeStageButton(d);
    this.btn = (label, on, size) => mk(label, on, size, { fallback: 'dialog' });
    const s = d.root.style;
    // a fixed, full-window MODAL overlay at the in-game modal z-layer (20), like the
    // ConfirmView. The canvas DialogForm is kfFrmAutoTakedown (EXCEPT the modeless LAG ALERT —
    // see `modeless` below), so a tap OUTSIDE the dialog dismisses it. pointer-events:all so the
    // click reaches us (canvas gets none).
    s.position = 'fixed';
    s.inset = '0';
    s.zIndex = '20';
    s.color = COL_FG;
    s.fontFamily = FONT;
    s.userSelect = 'none';
    s.pointerEvents = 'all';
    s.overflow = 'hidden';
    // MODELESS (the LAG ALERT): the canvas lag DialogForm is NON-autotakedown — it ignores
    // outside pen-down / Esc and waits for a real Yes/No press — so we do NOT register the
    // backdrop / Esc dismiss handlers at all (no stray onResult(false) → spurious wire vote).
    // The default (autotakedown) boxes register them, exactly as before.
    if (d.modeless !== true) {
      d.root.addEventListener('pointerdown', this.onBackdrop);
      window.addEventListener('keydown', this.onKeyDown);
    }
    window.addEventListener('resize', this.onResize);
    this.render();
  }

  private readonly onResize = (): void => {
    this.render();
  };

  // a tap NOT on the dialog → FALSE (No / keep-playing). The canvas kfFrmAutoTakedown
  // (form.ts:743/758) hardcodes EndForm(kidcCancel) on an outside pen-down / any key —
  // it does NOT take the form default control — and DoModal reports m_idcLast === kidcOk,
  // which is false (m_idcLast is set only on a real control selection). So an outside tap
  // is kidcCancel ⇒ false for BOTH the OK-only and the Yes/No query forms. This matters:
  // the resign/observe queries route this bool, so a stray dismiss must NOT report YES
  // (which would resign the game). Matches the sibling ConfirmView, same AutoTakedown.
  private readonly onBackdrop = (e: PointerEvent): void => {
    const stage = this.d.root.querySelector('[data-msgbox-stage]');
    if (stage !== null && e.target instanceof Node && stage.contains(e.target)) return;
    this.resolve(false);
  };

  // Esc → kidcCancel ⇒ false (the kfFrmAutoTakedown result; see onBackdrop).
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.resolve(false);
    }
  };

  destroy(): void {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKeyDown);
    this.d.root.removeEventListener('pointerdown', this.onBackdrop);
  }

  /** report the choice ONCE (the host unmounts + routes to the continuation). */
  private resolve(fOk: boolean): void {
    if (this.resolved) return;
    this.resolved = true;
    this.d.onResult(fOk);
  }

  // ── the faithful design stage (forms.ini: the message-box form is 140×50 + self-size) ──
  // The SAME fixed magnification family as the other in-game DOM forms (chrome.ts
  // formScale / at), at the message-box form's own 140×50 size.
  render(): void {
    const s = formScale();
    const query = this.d.variant === 'query';

    // SELF-SIZE the body like the canvas: the multiline kidcMessage label grows its
    // height, the form grows by the same delta, the buttons drop by that delta, and the
    // form re-centres. We measure the wrapped message in CSS to compute the extra height.
    const innerW = MSG.w * s; // the message box width in CSS px
    const lineH = Math.round(7 * s); // ≈ kifntShadow line height (design units → px)
    const fontPx = Math.round(6 * s);
    const lines = this.measureLines(this.d.message, innerW, fontPx, lineH);
    const naturalGrow = Math.max(MSG.h * s, lines * lineH) - MSG.h * s; // canvas rcNew.Height - rcOld.Height
    // CLAMP the growth so the whole stage (incl. the OK/Yes-No buttons) fits the viewport:
    // a very long message on a short window would otherwise push the buttons below the
    // bottom edge (overflow:hidden, no scroll) and strand them. Cap grow + scroll the body.
    const maxGrow = Math.max(0, rootBox(this.d.root).height - 8 * s - FORM.h * s);
    const grow = Math.min(naturalGrow, maxGrow);
    const msgScrolls = naturalGrow > grow;
    const msgPxH = MSG.h * s + grow; // the (possibly capped) body height (CSS px)
    const stageW = FORM.w * s;
    const stageH = FORM.h * s + grow; // the form grows by the same (capped) delta

    const borderCol = this.d.whiteBorder ? COL_BORDER_WHITE : COL_BORDER_BLACK;

    // the stage = the DialogForm: a BORDER (black/white) frame; inside it a RED TITLE BAR
    // (top, kcyTitle tall) and a DOUBLE-shadow translucent body (canvas shows through).
    const stage = el('div', {
      position: 'absolute',
      width: `${stageW}px`,
      height: `${stageH}px`,
      backgroundColor: borderCol, // the DrawBorder frame (shows at the edges)
      boxSizing: 'border-box',
    });
    stage.dataset.msgboxStage = '1';

    // the DOUBLE-shadow body (kiclrShadow2x ⇒ two ShadowHelper passes, ≈0.63 black) — below
    // the title bar, inset by the border. The live canvas behind shows through, darkened.
    stage.append(
      el('div', {
        position: 'absolute',
        left: `${BORDER * s}px`,
        top: `${CY_TITLE * s}px`,
        width: `${(FORM.w - 2 * BORDER) * s}px`,
        height: `${stageH - (CY_TITLE + BORDER) * s}px`,
        background: 'rgba(0, 0, 0, 0.63)',
      }),
    );

    // the RED TITLE BAR (SetTitleColor(kiclrRed)) — from the top border to kcyTitle.
    stage.append(
      el('div', {
        position: 'absolute',
        left: `${BORDER * s}px`,
        top: `${BORDER * s}px`,
        width: `${(FORM.w - 2 * BORDER) * s}px`,
        height: `${(CY_TITLE - BORDER) * s}px`,
        background: COL_TITLE_BAR,
      }),
    );

    // kidcTitle (61,2,18,0) center kifntTitle — the runtime title, WHITE on the red bar.
    // The Eurostile/Quadtrangle web face is wider than the bitmap font and the canvas
    // centres the label on the form, so span the FULL inner width (centred), don't clip.
    const title = formTitle(this.d.title, s, {
      fontPx,
      color: '#fff',
      textShadow: '0 1px 2px rgba(0,0,0,0.65)',
    });
    title.dataset.msgboxTitle = '1';
    stage.append(at(title, s, BORDER, TITLE.y, FORM.w - 2 * BORDER));

    // kidcMessage (6,15,128,8) center multiline kifntShadow — the self-sized body text.
    const msg = this.message(this.d.message, fontPx, lineH);
    msg.dataset.msgboxMessage = '1';
    stage.append(at(msg, s, MSG.x, MSG.y, MSG.w));
    msg.style.height = `${msgPxH}px`;
    if (msgScrolls) msg.style.overflowY = 'auto'; // a too-long message scrolls; buttons stay on-screen

    // the buttons drop by `grow` (the canvas rcButton.Offset(0, delta)). Each button is SIZED
    // to its label via the shared buttonWidthU (minW=30 for the message-box buttons) then
    // CENTRED — a single OK, or a Yes/No pair with an even gap — so the WI-font label keeps
    // clear margin inside the 9-slice frame (never a hand-picked literal width).
    const btnTop = OK_ONLY.y * s + grow;
    if (query) {
      // kidcOk "Yes" → true; kidcCancel "No" → false. Centre the pair on the 140-wide form.
      const wYes = buttonWidthU('Yes', YES.h, s, 30);
      const wNo = buttonWidthU('No', NO.h, s, 30);
      const gapU = 6;
      let bx = Math.max(BORDER, (FORM.w - (wYes + wNo + gapU)) / 2);
      const yes = this.btn('Yes', () => this.resolve(true), { w: wYes * s, h: YES.h * s });
      yes.dataset.msgboxYes = '1';
      Object.assign(yes.style, { position: 'absolute', left: `${bx * s}px`, top: `${btnTop}px` });
      stage.append(yes);
      bx += wYes + gapU;
      const no = this.btn('No', () => this.resolve(false), { w: wNo * s, h: NO.h * s });
      no.dataset.msgboxNo = '1';
      Object.assign(no.style, { position: 'absolute', left: `${bx * s}px`, top: `${btnTop}px` });
      stage.append(no);
    } else {
      // kidfMessageBox: a single centred OK → true (the only / default control).
      const wOk = buttonWidthU('OK', OK_ONLY.h, s, 30);
      const ok = this.btn('OK', () => this.resolve(true), { w: wOk * s, h: OK_ONLY.h * s });
      ok.dataset.msgboxOk = '1';
      Object.assign(ok.style, {
        position: 'absolute',
        left: `${((FORM.w - wOk) / 2) * s}px`,
        top: `${btnTop}px`,
      });
      stage.append(ok);
    }

    // CENTRE the stage on the screen (the canvas re-centres on the whole DIB — NOT
    // gob-anchored). misc.cpp:502-560 yNew = (siz.cy - rcForm.Height()) / 2.
    const { width: vw, height: vh } = rootBox(this.d.root);
    stage.style.left = `${Math.round(Math.max(0, (vw - stageW) / 2))}px`;
    stage.style.top = `${Math.round(Math.max(0, (vh - stageH) / 2))}px`;

    this.d.root.replaceChildren(stage);
  }

  // ── widgets (the title + the 9-slice button are the chrome.ts primitives) ───────
  // the kidcMessage body (kifntShadow) — multiline, centred, NO clipping (it self-sizes).
  private message(text: string, fontPx: number, lineH: number): HTMLElement {
    const e = el('div', {
      fontFamily: FONT,
      fontSize: `${fontPx}px`,
      lineHeight: `${lineH}px`,
      color: COL_FG,
      textShadow: shadow,
      textAlign: 'center',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflow: 'visible',
    });
    e.textContent = text;
    return e;
  }

  // measure how many wrapped lines `text` takes at `width` CSS px — to self-size the body.
  private measureLines(text: string, width: number, fontPx: number, lineH: number): number {
    if (typeof document === 'undefined') return 1;
    const m = el('div', {
      position: 'absolute',
      visibility: 'hidden',
      left: '-9999px',
      top: '0',
      width: `${width}px`,
      fontFamily: FONT,
      fontSize: `${fontPx}px`,
      lineHeight: `${lineH}px`,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    });
    m.textContent = text;
    document.body.appendChild(m);
    const lines = Math.max(1, Math.round(m.getBoundingClientRect().height / lineH));
    m.remove();
    return lines;
  }
}
